import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { storage } from '../../utils/storage';
import { ROUTES } from '../../constants/routes';

interface InactivityDetectorProps {
  timeoutInMinutes?: number;
  warningInMinutes?: number;
  onWarning?: () => void;
  onTimeout?: () => void;
}

export function InactivityDetector({
  timeoutInMinutes = 60, // Default 60 minutes
  warningInMinutes = 55, // Warning 5 minutes before timeout
  onWarning,
  onTimeout
}: InactivityDetectorProps) {
  const navigate = useNavigate();
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const warningIdRef = useRef<NodeJS.Timeout | null>(null);
  const hasWarnedRef = useRef(false);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigateRef = useRef(navigate);

  // Update navigate ref without causing dependency chain issues
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const handleLogout = useCallback(() => {
    // Clear all storage including activity timestamp
    storage.clearLastActivity();
    storage.clearAll();

    // Call custom timeout handler if provided
    if (onTimeout) {
      onTimeout();
    }

    // Navigate to login page with replace to avoid history issues
    navigateRef.current(ROUTES.LOGIN, { replace: true });
  }, [onTimeout]);

  const updateLastActivity = useCallback(() => {
    storage.updateLastActivity();
  }, []);

  const checkInactivity = useCallback(() => {
    const lastActivityTime = storage.getLastActivity();
    if (!lastActivityTime) {
      updateLastActivity();
      return;
    }

    const now = Date.now();
    const elapsedMinutes = (now - lastActivityTime) / 1000 / 60;

    // Check if we should show warning
    if (!hasWarnedRef.current && elapsedMinutes >= warningInMinutes && elapsedMinutes < timeoutInMinutes) {
      hasWarnedRef.current = true;
      if (onWarning) {
        onWarning();
      }
    }

    // Check if we should logout
    if (elapsedMinutes >= timeoutInMinutes) {
      handleLogout();
    }
  }, [warningInMinutes, timeoutInMinutes, onWarning, handleLogout, updateLastActivity]);

  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    if (warningIdRef.current) {
      clearTimeout(warningIdRef.current);
    }

    // Reset warning flag
    hasWarnedRef.current = false;

    // Update last activity timestamp
    updateLastActivity();

    // Set warning timer (if warning callback provided)
    if (onWarning && warningInMinutes < timeoutInMinutes) {
      warningIdRef.current = setTimeout(() => {
        checkInactivity();
      }, warningInMinutes * 60 * 1000);
    }

    // Set logout timer
    timeoutIdRef.current = setTimeout(() => {
      checkInactivity();
    }, timeoutInMinutes * 60 * 1000);
  }, [timeoutInMinutes, warningInMinutes, onWarning, updateLastActivity, checkInactivity]);

  useEffect(() => {
    // Events to track user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'input',
      'focus'
    ];

    const handleUserActivity = () => {
      resetTimers();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible - check if we should logout immediately
        checkInactivity();
        // If still logged in, reset timers
        const authData = storage.getAuthData();
        if (authData && authData.access_token) {
          resetTimers();
        }
      }
    };

    const handleFocus = () => {
      // Window regained focus - check inactivity immediately
      checkInactivity();
      // If still logged in, reset timers
      const authData = storage.getAuthData();
      if (authData && authData.access_token) {
        resetTimers();
      }
    };

    // Only set up inactivity detection if user is logged in
    const authData = storage.getAuthData();
    if (authData && authData.access_token) {  // Note: adjust property name based on your auth structure
      // Add event listeners
      events.forEach(event => {
        document.addEventListener(event, handleUserActivity);
      });

      // Add visibility change listener for when tab becomes active
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);

      // Check inactivity immediately on mount (handles page refresh after sleep)
      checkInactivity();

      // Start initial timer
      resetTimers();

      // Set up periodic check every 30 seconds to handle edge cases
      checkIntervalRef.current = setInterval(() => {
        checkInactivity();
      }, 30000); // Check every 30 seconds

      // Cleanup
      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleUserActivity);
        });
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
        }
        if (warningIdRef.current) {
          clearTimeout(warningIdRef.current);
        }
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
        }
      };
    } else {
      // Clear activity timestamp if not logged in
      storage.clearLastActivity();
    }
  }, [resetTimers, checkInactivity]);

  // No UI rendering needed
  return null;
}